import type { AnalyticsEventProps, AnalyticsEventValue } from '@/shared/lib/analytics/types'

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const NUMERIC_SEGMENT = /^\d+$/
const TOKEN_SEGMENT = /^[A-Za-z0-9_-]{24,}$/
const EMAIL = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const PROP_KEY = /^[a-z][a-z0-9_]{0,39}$/
const FORBIDDEN_PROP_KEY = /email|token|password|phone|iban|name|address|matricule/i
const MAX_STRING_LENGTH = 100

export function sanitizePath(pathname: string): string {
  const withoutQuery = pathname.split(/[?#]/)[0] ?? ''
  const segments = withoutQuery.split('/').map((segment) => {
    if (UUID_SEGMENT.test(segment) || NUMERIC_SEGMENT.test(segment)) {
      return ':id'
    }

    return TOKEN_SEGMENT.test(segment) ? ':token' : segment
  })
  const path = segments.join('/')

  return path === '' ? '/' : path
}

function sanitizeValue(value: unknown): AnalyticsEventValue | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    return value.replace(EMAIL, '[email]').slice(0, MAX_STRING_LENGTH)
  }

  return null
}

export function sanitizeEventProps(props: AnalyticsEventProps | undefined): AnalyticsEventProps | undefined {
  if (props === undefined) {
    return undefined
  }

  const out: Record<string, AnalyticsEventValue> = {}
  for (const [key, raw] of Object.entries(props)) {
    if (!PROP_KEY.test(key) || FORBIDDEN_PROP_KEY.test(key)) {
      continue
    }
    const value = sanitizeValue(raw)
    if (value !== null) {
      out[key] = value
    }
  }

  return Object.keys(out).length === 0 ? undefined : out
}
